import { Intent as BlueprintIntent } from '@blueprintjs/core';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  LoadingIcon,
  Modal,
  Selector,
} from 'vet-bones/design-system/components';
import { SelectItem } from 'vet-bones/design-system/utils/select';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import {
  TRANSFER_ORGANIZATION_DIALOG_CANCEL,
  TRANSFER_ORGANIZATION_DIALOG_SELECTOR,
  TRANSFER_ORGANIZATION_DIALOG_SELECTOR_ITEM,
  TRANSFER_ORGANIZATION_DIALOG_SUBMIT,
} from 'src/ui/constants/testIds';
import { queueMessage, queueNotification } from 'src/ui/features/notifications';
import { DialogType, closedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import { useTransferOrganizationOwnershipMutation } from 'src/ui/graph/types';
import { useProfile } from 'src/ui/hooks/connection';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import {
  useInvalidateOrganization,
  useInvalidateOrganizationMembers,
  useOrganization,
} from 'src/ui/hooks/organization';
import { isDialogOpenSelector } from 'src/ui/selectors/isDialogOpen';
import * as copy from 'src/ui/templates/copy';

const isOpenSelector = isDialogOpenSelector(DialogType.TRANSFER_ORGANIZATION);

export const TransferOrganizationDialog: React.VFC = () => {
  const dispatch = useAppDispatch();
  const { org = '' } = useParams<{ org?: string }>();

  const { data: profile } = useProfile();
  const { membersByEmail } = useOrganization(org);
  const invalidateOrg = useInvalidateOrganization(org);
  const invalidateMembers = useInvalidateOrganizationMembers(org);

  const memberItems = React.useMemo(
    () =>
      Object.values(membersByEmail)
        .filter((member) => member.email !== profile?.profile?.email)
        .map<SelectItem<null>>((member) => ({
          value: member.email,
          text: `${member.firstName} ${member.lastName}`,
          subText: member.email,
        })),
    [membersByEmail, profile]
  );

  const isOpen = useAppSelector(isOpenSelector);
  const onError = useOnError();
  const onMutate = useOnMutate();

  const [owner, setOwner] = React.useState<string>('');

  React.useEffect(() => {
    if (isOpen) {
      setOwner('');
    }
  }, [isOpen]);

  const {
    isLoading,
    mutate: transferOwnership,
  } = useTransferOrganizationOwnershipMutation(graphQLClient, {
    onMutate,
    onError,
    onSuccess: (data) => {
      invalidateOrg();
      invalidateMembers();

      if (!data.transferOrganizationOwnership?.success) {
        dispatch(
          queueNotification(
            data.transferOrganizationOwnership?.error ||
              copy.components.organization.transferDialog.notifications.error
          )
        );
        return;
      }

      dispatch(
        queueMessage({
          intent: BlueprintIntent.SUCCESS,
          message:
            copy.components.organization.transferDialog.notifications.success,
        })
      );
      handleClose();
    },
  });

  const handleClose = () => {
    dispatch(closedDialog());
    setOwner('');
  };

  const handleSubmit = () => {
    if (!owner || isLoading) {
      return;
    }

    transferOwnership({ org_domain: org, email: owner });
  };

  const isSubmitDisabled = !owner || isLoading;

  return (
    <Modal
      canOutsideClickClose={false}
      className="organization-dialog"
      footerActions={
        <>
          <Button
            onClick={handleClose}
            size="sm"
            testId={TRANSFER_ORGANIZATION_DIALOG_CANCEL}
            variant="ghost-gray"
          >
            {copy.components.organization.transferDialog.buttons.cancel}
          </Button>
          <Button
            disabled={isSubmitDisabled}
            leftIcon={isLoading ? LoadingIcon : undefined}
            onClick={handleSubmit}
            size="sm"
            testId={TRANSFER_ORGANIZATION_DIALOG_SUBMIT}
            variant="primary-gray"
          >
            {copy.components.organization.transferDialog.buttons.submit}
          </Button>
        </>
      }
      headerSize="lg"
      isOpen={isOpen}
      onClose={handleClose}
      title={copy.components.organization.transferDialog.title}
    >
      <div className="organization-dialog-body">
        <Selector.Single<null, SelectItem, null | SelectItem>
          chipInputProps={{
            className: 'organization-dialog-chip-selector',
            label: copy.components.organization.dialog.inputs.owner.label,
            size: 'md',
            testId: TRANSFER_ORGANIZATION_DIALOG_SELECTOR,
            values: owner ? [owner] : [],
          }}
          filterable
          items={memberItems}
          itemTestId={TRANSFER_ORGANIZATION_DIALOG_SELECTOR_ITEM}
          onSelect={(selected) => setOwner(selected?.value || '')}
          popoverProps={{
            portalClassName: 'organization-dialog-menu-portal',
          }}
          selected={memberItems.find((item) => item.value === owner) || null}
        />
      </div>
    </Modal>
  );
};
